import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useLang } from '../../context/LangContext';
import { COLORS } from '../../lib/constants';
import NotificationBell from '../../components/NotificationBell';

const NAV = [
  { id: 'dashboard', icon: '📊', labelAr: 'الرئيسية', labelEn: 'Dashboard' },
  { id: 'bookings', icon: '📅', labelAr: 'الحجوزات', labelEn: 'Bookings' },
  { id: 'services', icon: '✂️', labelAr: 'الخدمات', labelEn: 'Services' },
  { id: 'staff', icon: '👥', labelAr: 'الخبراء', labelEn: 'Experts' },
  { id: 'slots', icon: '🔒', labelAr: 'الأوقات', labelEn: 'Slots' },
  { id: 'hours', icon: '🕐', labelAr: 'ساعات العمل', labelEn: 'Hours' },
  { id: 'notify', icon: '📢', labelAr: 'إشعارات', labelEn: 'Notify' },
  { id: 'profile', icon: '🏪', labelAr: 'المكان', labelEn: 'Profile' },
];

const STATUS_COLOR = { pending: '#F59E0B', confirmed: '#3B82F6', completed: COLORS.success, cancelled: COLORS.error };
const STATUS_AR = { pending: 'قيد الانتظار', confirmed: 'مؤكد', completed: 'مكتمل', cancelled: 'ملغي' };
const STATUS_EN = { pending: 'Pending', confirmed: 'Confirmed', completed: 'Completed', cancelled: 'Cancelled' };

export default function BusinessApp() {
  const { profile, signOut } = useAuth();
  const { isRtl, toggleLang, lang } = useLang();
  const [screen, setScreen] = useState('dashboard');
  const [business, setBusiness] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [actionType, setActionType] = useState(null);

  useEffect(() => { fetchAll(); }, [profile.id]);

  const fetchAll = async () => {
    const { data: biz } = await supabase.from('businesses').select('*').eq('owner_id', profile.id).single();
    setBusiness(biz);
    if (biz) {
      const [bRes, sRes, stRes] = await Promise.all([
        supabase.from('bookings').select('*, services(name_ar, name_en), staff(name_ar, name_en, avatar_url), profiles(full_name, phone)').eq('business_id', biz.id).order('created_at', { ascending: false }),
        supabase.from('services').select('*').eq('business_id', biz.id),
        supabase.from('staff').select('*').eq('business_id', biz.id),
      ]);
      setBookings(bRes.data || []);
      setServices(sRes.data || []);
      setStaff(stRes.data || []);
    }
    setLoading(false);
  };

  const updateBookingStatus = async (id, status, note) => {
    const { data, error } = await supabase.rpc('update_booking_status_business', {
      p_booking_id: id,
      p_new_status: status,
      p_note: note || null,
    });
    if (error || !data || data.success === false) {
      alert(isRtl ? 'تعذر تحديث حالة الحجز، حاول مرة أخرى' : 'Could not update booking status, please try again');
      return;
    }
    setBookings(prev => prev.map(b => b.id === id ? { ...b, status } : b));
    setSelectedBooking(null);
    setActionNote('');
    setActionType(null);
  };

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Cairo, sans-serif', color: COLORS.primary }}>{isRtl ? 'جارٍ التحميل...' : 'Loading...'}</div>;

  return (
    <div style={{ maxWidth: '100%', margin: '0 auto', background: COLORS.bg, minHeight: '100vh', fontFamily: 'Cairo, sans-serif', direction: isRtl ? 'rtl' : 'ltr' }}>

      {/* نافذة تفاصيل الحجز */}
      {selectedBooking && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 24, width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text }}>{isRtl ? 'تفاصيل الحجز' : 'Booking Details'}</div>
              <button onClick={() => { setSelectedBooking(null); setActionType(null); setActionNote(''); }} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: COLORS.textMuted }}>✕</button>
            </div>

            {/* بيانات العميل */}
            <div style={{ background: COLORS.primaryLight, borderRadius: 14, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primaryDark, marginBottom: 8 }}>{isRtl ? 'بيانات العميل' : 'Customer Info'}</div>
              <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 4 }}>👤 {selectedBooking.profiles && selectedBooking.profiles.full_name}</div>
              <div style={{ fontSize: 13, color: COLORS.text }}>📞 {selectedBooking.profiles && selectedBooking.profiles.phone}</div>
            </div>

            {/* بيانات الخبير */}
            {selectedBooking.staff && (
              <div style={{ background: '#F0FDF4', borderRadius: 14, padding: 14, marginBottom: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: COLORS.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {selectedBooking.staff.avatar_url ? <img src={selectedBooking.staff.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '👤'}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{isRtl ? selectedBooking.staff.name_ar : (selectedBooking.staff.name_en || selectedBooking.staff.name_ar)}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>{isRtl ? 'الخبير المحدد' : 'Selected Expert'}</div>
                </div>
              </div>
            )}

            {/* تفاصيل الحجز */}
            <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
              {[
                { icon: '✂️', label: isRtl ? 'الخدمة' : 'Service', value: isRtl ? (selectedBooking.services && selectedBooking.services.name_ar) : (selectedBooking.services && (selectedBooking.services.name_en || selectedBooking.services.name_ar)) },
                { icon: '📅', label: isRtl ? 'التاريخ' : 'Date', value: selectedBooking.booking_date },
                { icon: '🕐', label: isRtl ? 'الوقت' : 'Time', value: selectedBooking.booking_time && selectedBooking.booking_time.slice(0, 5) },
                { icon: '💰', label: isRtl ? 'السعر' : 'Price', value: `${selectedBooking.price} ${isRtl ? 'ر.س' : 'SAR'}` },
                selectedBooking.is_mobile_service && { icon: '🚗', label: isRtl ? 'خدمة متنقلة' : 'Mobile Service', value: selectedBooking.client_address || (isRtl ? 'نعم' : 'Yes') },
                selectedBooking.notes && { icon: '📝', label: isRtl ? 'ملاحظات العميل' : 'Customer Notes', value: selectedBooking.notes },
              ].filter(Boolean).map((item, i, arr) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < arr.length - 1 ? `1px solid ${COLORS.border}` : 'none' }}>
                  <span style={{ fontSize: 12, color: COLORS.textMuted }}>{item.icon} {item.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, maxWidth: '60%', textAlign: isRtl ? 'left' : 'right' }}>{item.value}</span>
                </div>
              ))}
            </div>

            {/* خانة الملاحظة */}
            {actionType && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>
                  {actionType === 'confirmed' ? (isRtl ? 'ملاحظة للعميل (اختياري)' : 'Note to customer (optional)') : (isRtl ? 'سبب الرفض (اختياري)' : 'Rejection reason (optional)')}
                </div>
                <textarea value={actionNote} onChange={e => setActionNote(e.target.value)} placeholder={isRtl ? 'اكتب ملاحظتك هنا...' : 'Write your note here...'} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 10, fontSize: 13, fontFamily: 'Cairo, sans-serif', direction: isRtl ? 'rtl' : 'ltr', outline: 'none', resize: 'none', height: 70, boxSizing: 'border-box' }} />
              </div>
            )}

            {/* الأزرار */}
            {selectedBooking.status === 'pending' && !actionType && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setActionType('confirmed')} style={{ flex: 1, padding: '11px 0', background: COLORS.success, border: 'none', borderRadius: 12, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                  ✓ {isRtl ? 'قبول' : 'Accept'}
                </button>
                <button onClick={() => setActionType('cancelled')} style={{ flex: 1, padding: '11px 0', background: COLORS.error, border: 'none', borderRadius: 12, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                  ✕ {isRtl ? 'رفض' : 'Reject'}
                </button>
              </div>
            )}

            {actionType && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setActionType(null); setActionNote(''); }} style={{ flex: 1, padding: '11px 0', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, cursor: 'pointer', fontSize: 13, fontFamily: 'Cairo, sans-serif' }}>
                  {isRtl ? 'إلغاء' : 'Cancel'}
                </button>
                <button onClick={() => updateBookingStatus(selectedBooking.id, actionType, actionNote)} style={{ flex: 1, padding: '11px 0', background: actionType === 'confirmed' ? COLORS.success : COLORS.error, border: 'none', borderRadius: 12, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                  {actionType === 'confirmed' ? (isRtl ? '✓ تأكيد القبول' : '✓ Confirm Accept') : (isRtl ? '✕ تأكيد الرفض' : '✕ Confirm Reject')}
                </button>
              </div>
            )}

            {selectedBooking.status === 'confirmed' && (
              <button onClick={() => updateBookingStatus(selectedBooking.id, 'completed', '')} style={{ width: '100%', padding: '11px 0', background: COLORS.primary, border: 'none', borderRadius: 12, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                ✅ {isRtl ? 'تمت الخدمة' : 'Mark Complete'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, padding: '16px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{isRtl ? 'لوحة الشريك' : 'Partner Dashboard'}</div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>{business ? (isRtl ? business.name_ar : (business.name_en || business.name_ar)) : (isRtl ? 'لم يتم إعداد المكان' : 'Not set up yet')}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {profile?.id && <NotificationBell userId={profile.id} isRtl={isRtl} />}
          <button onClick={toggleLang} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 20, padding: '5px 12px', color: '#fff', cursor: 'pointer', fontSize: 12, fontFamily: 'Cairo, sans-serif', fontWeight: 700 }}>
            {lang === 'ar' ? 'EN' : 'ع'}
          </button>
          <button onClick={signOut} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '6px 12px', color: '#fff', cursor: 'pointer', fontSize: 12, fontFamily: 'Cairo, sans-serif' }}>
            {isRtl ? 'خروج' : 'Logout'}
          </button>
        </div>
      </div>

      <div style={{ padding: 16, paddingBottom: 80 }}>
        {/* Dashboard */}
        {screen === 'dashboard' && (
          <>
            {!business && (
              <div style={{ background: COLORS.primaryLight, borderRadius: 16, padding: 20, marginBottom: 16, textAlign: 'center', border: `1px solid ${COLORS.primaryBorder}` }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🏪</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.primaryDark, marginBottom: 8 }}>{isRtl ? 'أضف بيانات مكانك أولاً' : 'Set up your business first'}</div>
                <button onClick={() => setScreen('profile')} style={{ background: COLORS.primary, border: 'none', borderRadius: 12, padding: '10px 20px', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                  {isRtl ? 'إعداد المكان ←' : 'Set up ←'}
                </button>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              {[
                { icon: '⏳', num: bookings.filter(b => b.status === 'pending').length, labelAr: 'بانتظار القبول', labelEn: 'Pending' },
                { icon: '✅', num: bookings.filter(b => b.status === 'completed').length, labelAr: 'مكتملة', labelEn: 'Completed' },
                { icon: '✂️', num: services.length, labelAr: 'الخدمات', labelEn: 'Services' },
                { icon: '💰', num: bookings.filter(b => b.status === 'completed').reduce((s, b) => s + (b.price || 0), 0), labelAr: 'إجمالي ر.س', labelEn: 'Total SAR' },
              ].map((s, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 14, padding: 16, border: `1px solid ${COLORS.border}`, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>{s.icon}</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: COLORS.primary }}>{s.num}</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>{isRtl ? s.labelAr : s.labelEn}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 10 }}>{isRtl ? 'آخر الحجوزات' : 'Recent Bookings'}</div>
            {bookings.slice(0, 5).map(b => (
              <div key={b.id} onClick={() => { setSelectedBooking(b); setActionType(null); setActionNote(''); }} style={{ background: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, border: `1px solid ${b.status === 'pending' ? '#FCD34D' : COLORS.border}`, cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{b.profiles && b.profiles.full_name}</div>
                  <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 8, background: `${STATUS_COLOR[b.status]}20`, color: STATUS_COLOR[b.status], fontWeight: 700 }}>
                    {isRtl ? STATUS_AR[b.status] : STATUS_EN[b.status]}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                  {isRtl ? (b.services && b.services.name_ar) : (b.services && (b.services.name_en || b.services.name_ar))} · {b.booking_date} · {b.booking_time && b.booking_time.slice(0, 5)}
                </div>
              </div>
            ))}
          </>
        )}

        {/* الحجوزات */}
        {screen === 'bookings' && (
          <>
            <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text, marginBottom: 14 }}>{isRtl ? 'جميع الحجوزات' : 'All Bookings'}</div>
            {bookings.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>{isRtl ? 'لا توجد حجوزات' : 'No bookings yet'}</div>}
            {bookings.map(b => (
              <div key={b.id} onClick={() => { setSelectedBooking(b); setActionType(null); setActionNote(''); }} style={{ background: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, border: `1px solid ${b.status === 'pending' ? '#FCD34D' : COLORS.border}`, cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{b.profiles && b.profiles.full_name}</div>
                  <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 8, background: `${STATUS_COLOR[b.status]}20`, color: STATUS_COLOR[b.status], fontWeight: 700 }}>
                    {isRtl ? STATUS_AR[b.status] : STATUS_EN[b.status]}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 4 }}>
                  📞 {b.profiles && b.profiles.phone} · {isRtl ? (b.services && b.services.name_ar) : (b.services && (b.services.name_en || b.services.name_ar))}
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                  📅 {b.booking_date} · 🕐 {b.booking_time && b.booking_time.slice(0, 5)} · 💰 {b.price} {isRtl ? 'ر.س' : 'SAR'}
                </div>
                <div style={{ fontSize: 11, color: COLORS.primary, marginTop: 6, fontWeight: 600 }}>{isRtl ? 'اضغط للتفاصيل ←' : 'Tap for details →'}</div>
              </div>
            ))}
          </>
        )}

        {screen === 'services' && business && <ServicesManager business={business} services={services} setServices={setServices} isRtl={isRtl} />}
        {screen === 'staff' && business && <StaffManager business={business} staff={staff} setStaff={setStaff} services={services} isRtl={isRtl} />}
        {screen === 'slots' && business && <SlotsManager business={business} staff={staff} isRtl={isRtl} />}
        {screen === 'hours' && business && <WorkingHoursManager business={business} isRtl={isRtl} />}
        {screen === 'notify' && <CustomerNotifier business={business} isRtl={isRtl} />}
        {screen === 'profile' && <BusinessProfileEditor business={business} setBusiness={setBusiness} profile={profile} isRtl={isRtl} />}
      </div>

      {/* شريط التنقل */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: `1px solid ${COLORS.border}`, display: 'flex', padding: '8px 0 12px', zIndex: 100 }}>
        {NAV.map(item => (
          <button key={item.id} onClick={() => setScreen(item.id)} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, fontFamily: 'Cairo, sans-serif' }}>
            <span style={{ fontSize: 20 }}>{item.icon}</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: screen === item.id ? COLORS.primary : COLORS.textMuted }}>
              {isRtl ? item.labelAr : item.labelEn}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ServicesManager({ business, services, setServices, isRtl }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name_ar: '', name_en: '', price: '', duration_minutes: '' });

  const addService = async () => {
    if (!form.name_ar || !form.price || !form.duration_minutes) return;
    const { data } = await supabase.from('services').insert({ ...form, price: Number(form.price), duration_minutes: Number(form.duration_minutes), business_id: business.id }).select().single();
    if (data) { setServices(prev => [...prev, data]); setShowAdd(false); setForm({ name_ar: '', name_en: '', price: '', duration_minutes: '' }); }
  };

  const toggleService = async (id, is_active) => {
    await supabase.from('services').update({ is_active }).eq('id', id);
    setServices(prev => prev.map(s => s.id === id ? { ...s, is_active } : s));
  };

  const INPUT = { width: '100%', padding: '11px 13px', border: `1.5px solid ${COLORS.border}`, borderRadius: 10, fontSize: 13, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box', marginBottom: 10, direction: isRtl ? 'rtl' : 'ltr' };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text }}>{isRtl ? 'الخدمات' : 'Services'}</div>
        <button onClick={() => setShowAdd(!showAdd)} style={{ background: COLORS.primary, border: 'none', borderRadius: 10, padding: '8px 14px', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
          {isRtl ? '+ إضافة' : '+ Add'}
        </button>
      </div>
      {showAdd && (
        <div style={{ background: '#fff', borderRadius: 14, padding: 16, marginBottom: 14, border: `1px solid ${COLORS.primaryBorder}` }}>
          <input style={INPUT} placeholder={isRtl ? 'اسم الخدمة بالعربي *' : 'Service name in Arabic *'} value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} />
          <input style={INPUT} placeholder={isRtl ? 'اسم الخدمة بالإنجليزي' : 'Service name in English'} value={form.name_en} onChange={e => setForm({ ...form, name_en: e.target.value })} />
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...INPUT, flex: 1, marginBottom: 0 }} type="number" placeholder={isRtl ? 'السعر (ر.س)' : 'Price (SAR)'} value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
            <input style={{ ...INPUT, flex: 1, marginBottom: 0 }} type="number" placeholder={isRtl ? 'المدة (دقيقة)' : 'Duration (min)'} value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={addService} style={{ flex: 1, padding: '10px 0', background: COLORS.primary, border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>{isRtl ? 'حفظ' : 'Save'}</button>
            <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px 0', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, cursor: 'pointer', fontSize: 13, fontFamily: 'Cairo, sans-serif' }}>{isRtl ? 'إلغاء' : 'Cancel'}</button>
          </div>
        </div>
      )}
      {services.map(s => (
        <div key={s.id} style={{ background: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, border: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: s.is_active ? 1 : 0.5 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{isRtl ? s.name_ar : (s.name_en || s.name_ar)}</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>{s.price} {isRtl ? 'ر.س' : 'SAR'} · {s.duration_minutes} {isRtl ? 'دقيقة' : 'min'}</div>
          </div>
          <button onClick={() => toggleService(s.id, !s.is_active)} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: s.is_active ? '#FEF2F2' : '#F0FDF4', color: s.is_active ? COLORS.error : COLORS.success, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
            {s.is_active ? (isRtl ? 'إيقاف' : 'Disable') : (isRtl ? 'تفعيل' : 'Enable')}
          </button>
        </div>
      ))}
    </>
  );
}

function StaffManager({ business, staff, setStaff, services, isRtl }) {
  const [showAdd, setShowAdd] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [form, setForm] = useState({ name_ar: '', name_en: '', title_ar: '', title_en: '', avatar_url: '', experience_years: '' });
  const [linkedServices, setLinkedServices] = useState({});

  useEffect(() => {
    if (staff.length === 0) return;
    const fetch = async () => {
      const { data } = await supabase.from('staff_services').select('*').in('staff_id', staff.map(s => s.id));
      const map = {};
      (data || []).forEach(ss => {
        if (!map[ss.staff_id]) map[ss.staff_id] = [];
        map[ss.staff_id].push(ss.service_id);
      });
      setLinkedServices(map);
    };
    fetch();
  }, [staff]);

  const addStaff = async () => {
    if (!form.name_ar) return;
    const { data } = await supabase.from('staff').insert({ ...form, experience_years: Number(form.experience_years) || 0, business_id: business.id }).select().single();
    if (data) { setStaff(prev => [...prev, data]); setShowAdd(false); setForm({ name_ar: '', name_en: '', title_ar: '', title_en: '', avatar_url: '', experience_years: '' }); }
  };

  const toggleStaffService = async (staffId, serviceId, isLinked) => {
    if (isLinked) {
      await supabase.from('staff_services').delete().eq('staff_id', staffId).eq('service_id', serviceId);
      setLinkedServices(prev => ({ ...prev, [staffId]: (prev[staffId] || []).filter(id => id !== serviceId) }));
    } else {
      await supabase.from('staff_services').insert({ staff_id: staffId, service_id: serviceId });
      setLinkedServices(prev => ({ ...prev, [staffId]: [...(prev[staffId] || []), serviceId] }));
    }
  };

  const toggleActive = async (id, is_active) => {
    await supabase.from('staff').update({ is_active }).eq('id', id);
    setStaff(prev => prev.map(s => s.id === id ? { ...s, is_active } : s));
  };

  const INPUT = { width: '100%', padding: '11px 13px', border: `1.5px solid ${COLORS.border}`, borderRadius: 10, fontSize: 13, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box', marginBottom: 10, direction: isRtl ? 'rtl' : 'ltr' };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text }}>{isRtl ? 'الخبراء' : 'Experts'}</div>
        <button onClick={() => setShowAdd(!showAdd)} style={{ background: COLORS.primary, border: 'none', borderRadius: 10, padding: '8px 14px', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
          {isRtl ? '+ إضافة' : '+ Add'}
        </button>
      </div>

      {showAdd && (
        <div style={{ background: '#fff', borderRadius: 14, padding: 16, marginBottom: 14, border: `1px solid ${COLORS.primaryBorder}` }}>
          <input style={INPUT} placeholder={isRtl ? 'الاسم بالعربي *' : 'Name in Arabic *'} value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} />
          <input style={INPUT} placeholder={isRtl ? 'الاسم بالإنجليزي' : 'Name in English'} value={form.name_en} onChange={e => setForm({ ...form, name_en: e.target.value })} />
          <input style={INPUT} placeholder={isRtl ? 'المسمى الوظيفي' : 'Job Title'} value={form.title_ar} onChange={e => setForm({ ...form, title_ar: e.target.value })} />
          <input style={INPUT} placeholder={isRtl ? 'رابط الصورة' : 'Photo URL'} value={form.avatar_url} onChange={e => setForm({ ...form, avatar_url: e.target.value })} />
          <input style={INPUT} type="number" placeholder={isRtl ? 'سنوات الخبرة' : 'Years of experience'} value={form.experience_years} onChange={e => setForm({ ...form, experience_years: e.target.value })} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={addStaff} style={{ flex: 1, padding: '10px 0', background: COLORS.primary, border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>{isRtl ? 'حفظ' : 'Save'}</button>
            <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px 0', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, cursor: 'pointer', fontSize: 13, fontFamily: 'Cairo, sans-serif' }}>{isRtl ? 'إلغاء' : 'Cancel'}</button>
          </div>
        </div>
      )}

      {/* نافذة تفاصيل الخبير */}
      {selectedStaff && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 24, width: '100%', maxWidth: 400 }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ width: 80, height: 80, borderRadius: '50%', background: COLORS.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, margin: '0 auto 12px', overflow: 'hidden' }}>
                {selectedStaff.avatar_url ? <img src={selectedStaff.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '👤'}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.text }}>{isRtl ? selectedStaff.name_ar : (selectedStaff.name_en || selectedStaff.name_ar)}</div>
              <div style={{ fontSize: 13, color: COLORS.textMuted }}>{isRtl ? selectedStaff.title_ar : (selectedStaff.title_en || selectedStaff.title_ar)}</div>
              {selectedStaff.rating > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 2, marginTop: 8 }}>
                  {[1,2,3,4,5].map(star => <span key={star} style={{ fontSize: 18, color: star <= Math.round(selectedStaff.rating) ? '#F59E0B' : '#DDD' }}>★</span>)}
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#F59E0B', marginRight: 4 }}>{Number(selectedStaff.rating).toFixed(1)}</span>
                </div>
              )}
              {selectedStaff.experience_years > 0 && <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>🏆 {selectedStaff.experience_years} {isRtl ? 'سنوات خبرة' : 'years exp'}</div>}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, marginBottom: 8 }}>{isRtl ? 'الخدمات المرتبطة:' : 'Linked Services:'}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
              {services.map(srv => {
                const isLinked = (linkedServices[selectedStaff.id] || []).includes(srv.id);
                return (
                  <button key={srv.id} onClick={() => { toggleStaffService(selectedStaff.id, srv.id, isLinked); }} style={{ padding: '5px 10px', borderRadius: 8, border: `1.5px solid ${isLinked ? COLORS.primary : COLORS.border}`, background: isLinked ? COLORS.primaryLight : '#fff', color: isLinked ? COLORS.primary : COLORS.textMuted, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'Cairo, sans-serif' }}>
                    {isLinked ? '✓ ' : ''}{isRtl ? srv.name_ar : (srv.name_en || srv.name_ar)}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setSelectedStaff(null)} style={{ width: '100%', padding: '11px 0', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 12, cursor: 'pointer', fontSize: 14, fontFamily: 'Cairo, sans-serif' }}>
              {isRtl ? 'إغلاق' : 'Close'}
            </button>
          </div>
        </div>
      )}

      {staff.map(s => (
        <div key={s.id} onClick={() => setSelectedStaff(s)} style={{ background: '#fff', borderRadius: 16, padding: 16, marginBottom: 12, border: `1px solid ${COLORS.border}`, opacity: s.is_active ? 1 : 0.6, cursor: 'pointer' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ width: 52, height: 52, borderRadius: 12, background: COLORS.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {s.avatar_url ? <img src={s.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '👤'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{isRtl ? s.name_ar : (s.name_en || s.name_ar)}</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>{isRtl ? s.title_ar : (s.title_en || s.title_ar)}</div>
              {s.rating > 0 && <div style={{ fontSize: 12, color: '#F59E0B' }}>⭐ {Number(s.rating).toFixed(1)}</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button onClick={e => { e.stopPropagation(); toggleActive(s.id, !s.is_active); }} style={{ padding: '5px 10px', borderRadius: 8, border: 'none', background: s.is_active ? '#FEF2F2' : '#F0FDF4', color: s.is_active ? COLORS.error : COLORS.success, cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                {s.is_active ? (isRtl ? 'إيقاف' : 'Off') : (isRtl ? 'تفعيل' : 'On')}
              </button>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

function SlotsManager({ business, staff, isRtl }) {
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const ALL_TIMES = ['09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00','17:30','18:00','18:30','19:00','19:30','20:00'];

  const fetchBlocked = async (date) => {
    if (!date) return;
    setLoading(true);
    const query = supabase.from('blocked_slots').select('*').eq('business_id', business.id).eq('blocked_date', date);
    if (selectedStaff !== 'all') query.eq('staff_id', selectedStaff);
    const { data } = await query;
    setBlockedSlots(data || []);
    setLoading(false);
  };

  const toggleSlot = async (time) => {
    const existing = blockedSlots.find(s => s.blocked_time && s.blocked_time.slice(0,5) === time);
    if (existing) {
      await supabase.from('blocked_slots').delete().eq('id', existing.id);
      setBlockedSlots(prev => prev.filter(s => s.id !== existing.id));
    } else {
      const { data } = await supabase.from('blocked_slots').insert({
        business_id: business.id,
        staff_id: selectedStaff === 'all' ? null : selectedStaff,
        blocked_date: selectedDate,
        blocked_time: time + ':00',
        reason: isRtl ? 'محجوز خارجياً' : 'External booking',
      }).select().single();
      if (data) setBlockedSlots(prev => [...prev, data]);
    }
  };

  const isBlocked = (time) => blockedSlots.some(s => s.blocked_time && s.blocked_time.slice(0,5) === time);

  return (
    <>
      <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text, marginBottom: 4 }}>{isRtl ? 'إغلاق الأوقات' : 'Block Time Slots'}</div>
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 16 }}>{isRtl ? 'أغلق الأوقات المحجوزة خارج التطبيق' : 'Block times booked outside the app'}</div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>{isRtl ? 'الخبير' : 'Expert'}</div>
        <select value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)} style={{ width: '100%', padding: '11px 13px', border: `1.5px solid ${COLORS.border}`, borderRadius: 10, fontSize: 13, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box' }}>
          <option value="all">{isRtl ? 'جميع الخبراء' : 'All Experts'}</option>
          {staff.map(s => <option key={s.id} value={s.id}>{isRtl ? s.name_ar : (s.name_en || s.name_ar)}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>{isRtl ? 'التاريخ' : 'Date'}</div>
        <input type="date" value={selectedDate} onChange={e => { setSelectedDate(e.target.value); fetchBlocked(e.target.value); }} style={{ width: '100%', padding: '11px 13px', border: `1.5px solid ${COLORS.border}`, borderRadius: 10, fontSize: 13, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box' }} min={new Date().toISOString().split('T')[0]} />
      </div>
      {selectedDate && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>{isRtl ? 'اضغط لإغلاق أو فتح الوقت' : 'Tap to block or unblock'}</div>
          {loading ? <div style={{ textAlign: 'center', padding: 20, color: COLORS.textMuted }}>{isRtl ? 'جارٍ التحميل...' : 'Loading...'}</div> : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {ALL_TIMES.map(t => (
                <button key={t} onClick={() => toggleSlot(t)} style={{ padding: '8px 14px', borderRadius: 10, border: `1.5px solid ${isBlocked(t) ? COLORS.error : COLORS.border}`, background: isBlocked(t) ? '#FEF2F2' : '#fff', color: isBlocked(t) ? COLORS.error : COLORS.text, cursor: 'pointer', fontSize: 13, fontFamily: 'Cairo, sans-serif', fontWeight: 600 }}>
                  {isBlocked(t) ? '🔒 ' : ''}{t}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

function BusinessProfileEditor({ business, setBusiness, profile, isRtl }) {
  const GOOGLE_KEY = 'AIzaSyASl0qDM1rTxhYxud3HceuoLcsJNqRhFtQ';
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [form, setForm] = useState(business || { name_ar: '', name_en: '', description_ar: '', description_en: '', city: '', district: '', phone: '', whatsapp: '', type: 'fixed', logo_url: '', lat: '', lng: '', google_rating: null, google_reviews_count: null });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [googleUrl, setGoogleUrl] = useState('');
  const [fetchingGoogle, setFetchingGoogle] = useState(false);
  const [googleMsg, setGoogleMsg] = useState('');

  const fetchFromGoogle = async () => {
    if (!googleUrl) return;
    setFetchingGoogle(true);
    setGoogleMsg('');

    try {
      // استخراج الإحداثيات من الرابط مباشرة
      const coordMatch = googleUrl.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      const coord2Match = googleUrl.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);

      let lat = null, lng = null;
      if (coord2Match) {
        lat = parseFloat(coord2Match[1]);
        lng = parseFloat(coord2Match[2]);
      } else if (coordMatch) {
        lat = parseFloat(coordMatch[1]);
        lng = parseFloat(coordMatch[2]);
      }

      // استخراج اسم المكان من الرابط
      const nameMatch = googleUrl.match(/place\/([^/]+)\//);
      let name = '';
      if (nameMatch) {
        name = decodeURIComponent(nameMatch[1]).replace(/\+/g, ' ');
      }

      if (lat && lng) {
        // جلب التقييم من Google Places API عبر Edge Function
        const { data: result } = await supabase.functions.invoke('google-places', {
          body: { lat, lng, key: GOOGLE_KEY }
        });

        const rating = result && result.rating ? result.rating : null;
        const reviewsCount = result && result.reviews_count ? result.reviews_count : null;
        const placeName = (result && result.name) ? result.name : name;

        setForm(prev => ({
          ...prev,
          name_ar: placeName || prev.name_ar,
          lat: lat,
          lng: lng,
          google_rating: rating,
          google_reviews_count: reviewsCount,
        }));

        if (rating) {
          setGoogleMsg(isRtl ? `✅ تم سحب البيانات — التقييم: ${rating} (${reviewsCount} تقييم)` : `✅ Data fetched — Rating: ${rating} (${reviewsCount} reviews)`);
        } else {
          setGoogleMsg(isRtl ? `✅ تم سحب الموقع — Lat: ${lat.toFixed(4)}, Lng: ${lng.toFixed(4)}` : `✅ Location fetched — Lat: ${lat.toFixed(4)}, Lng: ${lng.toFixed(4)}`);
        }
      } else {
        setGoogleMsg(isRtl ? '⚠️ لم يتم استخراج الإحداثيات — تأكد من الرابط' : '⚠️ Could not extract coordinates');
      }
    } catch (err) {
      setGoogleMsg(isRtl ? '⚠️ خطأ في المعالجة' : '⚠️ Processing error');
    }
    setFetchingGoogle(false);
  };

  useEffect(() => {
    supabase.from('categories').select('*').order('sort_order').then(({ data }) => setCategories(data || []));
    if (business) {
      supabase.from('business_categories').select('category_id').eq('business_id', business.id).then(({ data }) => {
        setSelectedCategories((data || []).map(d => d.category_id));
      });
    }
  }, [business]);

  const toggleCategory = async (catId) => {
    if (!business) return;
    const isSelected = selectedCategories.includes(catId);
    if (isSelected) {
      await supabase.from('business_categories').delete().eq('business_id', business.id).eq('category_id', catId);
      setSelectedCategories(prev => prev.filter(id => id !== catId));
    } else {
      await supabase.from('business_categories').insert({ business_id: business.id, category_id: catId });
      setSelectedCategories(prev => [...prev, catId]);
    }
  };

  const save = async () => {
    setSaving(true);
    const saveData = { ...form, lat: form.lat ? Number(form.lat) : null, lng: form.lng ? Number(form.lng) : null };
    if (business) {
      const { data } = await supabase.from('businesses').update(saveData).eq('id', business.id).select().single();
      setBusiness(data);
    } else {
      const { data } = await supabase.from('businesses').insert({ ...saveData, owner_id: profile.id, status: 'pending' }).select().single();
      setBusiness(data);
    }
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const INPUT = { width: '100%', padding: '11px 13px', border: `1.5px solid ${COLORS.border}`, borderRadius: 10, fontSize: 13, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box', marginBottom: 12, direction: isRtl ? 'rtl' : 'ltr', background: '#fff' };
  const LABEL = { display: 'block', fontSize: 12, fontWeight: 700, color: COLORS.textMuted, marginBottom: 4 };

  return (
    <>
      <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text, marginBottom: 16 }}>{isRtl ? 'بيانات المكان' : 'Business Profile'}</div>
      {saved && <div style={{ background: '#F0FDF4', border: `1px solid #BBF7D0`, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: COLORS.success }}>✅ {isRtl ? 'تم الحفظ بنجاح' : 'Saved successfully'}</div>}

      {/* سحب من جوجل */}
      <div style={{ background: '#EFF6FF', borderRadius: 14, padding: 16, marginBottom: 16, border: '1px solid #BFDBFE' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1D4ED8', marginBottom: 8 }}>
          📍 {isRtl ? 'سحب البيانات من خرائط جوجل' : 'Import from Google Maps'}
        </div>
        <div style={{ fontSize: 11, color: '#3B82F6', marginBottom: 10 }}>
          {isRtl ? 'الصق رابط المكان من جوجل ماب لسحب التقييم والبيانات تلقائياً' : 'Paste Google Maps link to auto-import rating and data'}
        </div>
        <input value={googleUrl} onChange={e => setGoogleUrl(e.target.value)} placeholder="https://maps.app.goo.gl/..." style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #BFDBFE', borderRadius: 10, fontSize: 13, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box', marginBottom: 8, direction: 'ltr' }} />
        <button onClick={fetchFromGoogle} disabled={fetchingGoogle || !googleUrl} style={{ width: '100%', padding: '10px 0', background: fetchingGoogle ? '#93C5FD' : '#2563EB', border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
          {fetchingGoogle ? (isRtl ? 'جارٍ السحب...' : 'Fetching...') : (isRtl ? '🔄 سحب البيانات' : '🔄 Import Data')}
        </button>
        {googleMsg && <div style={{ marginTop: 8, fontSize: 12, color: googleMsg.startsWith('✅') ? COLORS.success : '#D97706', fontFamily: 'Cairo, sans-serif' }}>{googleMsg}</div>}
        {form.google_rating && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 13, color: '#1D4ED8', fontWeight: 700 }}>
              🌟 {isRtl ? 'تقييم جوجل:' : 'Google Rating:'} {form.google_rating} ({form.google_reviews_count} {isRtl ? 'تقييم' : 'reviews'})
            </div>
          </div>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: 16, border: `1px solid ${COLORS.border}`, marginBottom: 12 }}>
        <label style={LABEL}>{isRtl ? 'رابط الشعار' : 'Logo URL'}</label>
        <input style={INPUT} placeholder="https://..." value={form.logo_url || ''} onChange={e => setForm({ ...form, logo_url: e.target.value })} />
        {form.logo_url && <img src={form.logo_url} alt="" style={{ width: 64, height: 64, borderRadius: 12, objectFit: 'cover', marginBottom: 12 }} onError={e => e.target.style.display = 'none'} />}

        <label style={LABEL}>{isRtl ? 'اسم المكان بالعربي *' : 'Business name in Arabic *'}</label>
        <input style={INPUT} value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} />

        <label style={LABEL}>{isRtl ? 'اسم المكان بالإنجليزي' : 'Business name in English'}</label>
        <input style={INPUT} value={form.name_en || ''} onChange={e => setForm({ ...form, name_en: e.target.value })} />

        <label style={LABEL}>{isRtl ? 'الوصف بالعربي' : 'Description in Arabic'}</label>
        <textarea style={{ ...INPUT, resize: 'none', height: 70 }} value={form.description_ar || ''} onChange={e => setForm({ ...form, description_ar: e.target.value })} />

        <label style={LABEL}>{isRtl ? 'المدينة' : 'City'}</label>
        <input style={INPUT} value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} />

        <label style={LABEL}>{isRtl ? 'الحي' : 'District'}</label>
        <input style={INPUT} value={form.district || ''} onChange={e => setForm({ ...form, district: e.target.value })} />

        <label style={LABEL}>{isRtl ? 'رقم الجوال' : 'Phone'}</label>
        <input style={INPUT} value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} />

        <label style={LABEL}>{isRtl ? 'رابط الموقع على خرائط جوجل (Latitude)' : 'Google Maps Latitude'}</label>
        <input style={INPUT} type="number" placeholder="24.7136" value={form.lat || ''} onChange={e => setForm({ ...form, lat: e.target.value })} />

        <label style={LABEL}>{isRtl ? 'رابط الموقع على خرائط جوجل (Longitude)' : 'Google Maps Longitude'}</label>
        <input style={INPUT} type="number" placeholder="46.6753" value={form.lng || ''} onChange={e => setForm({ ...form, lng: e.target.value })} />

        <label style={LABEL}>{isRtl ? 'نوع الخدمة' : 'Service Type'}</label>
        <select style={{ ...INPUT, cursor: 'pointer' }} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
          <option value="fixed">{isRtl ? '🏠 مكان ثابت' : '🏠 Fixed Location'}</option>
          <option value="mobile">{isRtl ? '🚗 خدمة متنقلة' : '🚗 Mobile Service'}</option>
          <option value="hybrid">{isRtl ? '🏠🚗 ثابت + متنقل' : '🏠🚗 Fixed + Mobile'}</option>
        </select>

        {/* اختيار المجالات */}
        <label style={LABEL}>{isRtl ? 'المجالات (يمكن اختيار أكثر من مجال)' : 'Categories (multiple allowed)'}</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {categories.map(cat => {
            const isSelected = selectedCategories.includes(cat.id);
            return (
              <button key={cat.id} onClick={() => toggleCategory(cat.id)} style={{ padding: '7px 14px', borderRadius: 20, border: `1.5px solid ${isSelected ? COLORS.primary : COLORS.border}`, background: isSelected ? COLORS.primaryLight : '#fff', color: isSelected ? COLORS.primary : COLORS.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'Cairo, sans-serif', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span>{cat.icon}</span>
                <span>{isRtl ? cat.name_ar : cat.name_en}</span>
                {isSelected && <span>✓</span>}
              </button>
            );
          })}
        </div>
      </div>

      <button onClick={save} disabled={saving} style={{ width: '100%', padding: 14, background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
        {saving ? (isRtl ? 'جارٍ الحفظ...' : 'Saving...') : (isRtl ? 'حفظ البيانات ✓' : 'Save ✓')}
      </button>
    </>
  );
}

function WorkingHoursManager({ business, isRtl }) {
  const DAYS_AR = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
  const DAYS_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { load(); }, [business?.id]);

  const load = async () => {
    if (!business) return;
    const { data } = await supabase.from('working_hours').select('*').eq('business_id', business.id).is('staff_id', null);
    const map = [0, 1, 2, 3, 4, 5, 6].map(d => {
      const existing = (data || []).find(h => h.day_of_week === d);
      return existing || { day_of_week: d, open_time: '09:00', close_time: '20:00', is_closed: false };
    });
    setHours(map);
    setLoading(false);
  };

  const updateDay = (day, field, value) => {
    setHours(prev => prev.map(h => h.day_of_week === day ? { ...h, [field]: value } : h));
  };

  const save = async () => {
    setSaving(true);
    setResult(null);
    const { error: delError } = await supabase.from('working_hours').delete().eq('business_id', business.id).is('staff_id', null);
    if (!delError) {
      const { error: insError } = await supabase.from('working_hours').insert(
        hours.map(h => ({
          business_id: business.id,
          staff_id: null,
          day_of_week: h.day_of_week,
          open_time: h.open_time,
          close_time: h.close_time,
          is_closed: h.is_closed,
        }))
      );
      setResult({ ok: !insError });
    } else {
      setResult({ ok: false });
    }
    setSaving(false);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>{isRtl ? 'جارٍ التحميل...' : 'Loading...'}</div>;
  if (!business) return <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>{isRtl ? 'أعد بيانات مكانك أولاً' : 'Set up your business first'}</div>;

  return (
    <>
      <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text, marginBottom: 6 }}>{isRtl ? 'ساعات العمل' : 'Working Hours'}</div>
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 16 }}>{isRtl ? 'يعتمد عليها العملاء عند اختيار وقت الحجز' : 'Customers see these when picking a booking time'}</div>

      {result && (
        <div style={{ background: result.ok ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${result.ok ? '#BBF7D0' : '#FECACA'}`, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: result.ok ? COLORS.success : COLORS.error }}>
          {result.ok ? (isRtl ? '✅ تم الحفظ' : '✅ Saved') : (isRtl ? '⚠️ تعذر الحفظ' : '⚠️ Failed to save')}
        </div>
      )}

      {hours.map(h => (
        <div key={h.day_of_week} style={{ background: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, border: `1px solid ${COLORS.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: h.is_closed ? 0 : 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{isRtl ? DAYS_AR[h.day_of_week] : DAYS_EN[h.day_of_week]}</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: COLORS.textMuted }}>
              <input type="checkbox" checked={h.is_closed} onChange={e => updateDay(h.day_of_week, 'is_closed', e.target.checked)} />
              {isRtl ? 'إجازة' : 'Closed'}
            </label>
          </div>
          {!h.is_closed && (
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: COLORS.textMuted }}>{isRtl ? 'من' : 'From'}</label>
                <input type="time" value={h.open_time} onChange={e => updateDay(h.day_of_week, 'open_time', e.target.value)} style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'Cairo, sans-serif', boxSizing: 'border-box' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: COLORS.textMuted }}>{isRtl ? 'إلى' : 'To'}</label>
                <input type="time" value={h.close_time} onChange={e => updateDay(h.day_of_week, 'close_time', e.target.value)} style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'Cairo, sans-serif', boxSizing: 'border-box' }} />
              </div>
            </div>
          )}
        </div>
      ))}

      <button onClick={save} disabled={saving} style={{ width: '100%', padding: 14, marginTop: 6, background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
        {saving ? (isRtl ? 'جارٍ الحفظ...' : 'Saving...') : (isRtl ? 'حفظ ساعات العمل ✓' : 'Save Working Hours ✓')}
      </button>
    </>
  );
}

function CustomerNotifier({ business, isRtl }) {
  const [form, setForm] = useState({ title_ar: '', title_en: '', body_ar: '', body_en: '' });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!business || !form.title_ar || !form.body_ar) return;
    setSending(true);
    setResult(null);
    const { data, error } = await supabase.rpc('business_notify_customers', {
      p_business_id: business.id,
      p_title_ar: form.title_ar,
      p_title_en: form.title_en || null,
      p_body_ar: form.body_ar,
      p_body_en: form.body_en || null,
    });
    setSending(false);
    if (error || !data || data.success === false) {
      setResult({ ok: false, msg: isRtl ? 'حدث خطأ أثناء الإرسال' : 'Failed to send' });
    } else {
      setResult({ ok: true, msg: isRtl ? `تم الإرسال لـ ${data.count} عميل` : `Sent to ${data.count} customers` });
      setForm({ title_ar: '', title_en: '', body_ar: '', body_en: '' });
    }
  };

  const INPUT = { width: '100%', padding: '11px 13px', border: `1.5px solid ${COLORS.border}`, borderRadius: 10, fontSize: 13, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box', marginBottom: 12, direction: isRtl ? 'rtl' : 'ltr', background: '#fff' };
  const LABEL = { display: 'block', fontSize: 12, fontWeight: 700, color: COLORS.textMuted, marginBottom: 4 };

  if (!business) {
    return <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>{isRtl ? 'أعد بيانات مكانك أولاً' : 'Set up your business first'}</div>;
  }

  return (
    <>
      <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text, marginBottom: 6 }}>{isRtl ? 'إرسال إشعار لعملائك' : 'Notify Your Customers'}</div>
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 16 }}>{isRtl ? 'يصل الإشعار فوراً لكل من حجز معك سابقاً' : 'Reaches everyone who has booked with you instantly'}</div>

      {result && (
        <div style={{ background: result.ok ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${result.ok ? '#BBF7D0' : '#FECACA'}`, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: result.ok ? COLORS.success : COLORS.error }}>
          {result.ok ? '✅' : '⚠️'} {result.msg}
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 16, padding: 16, border: `1px solid ${COLORS.border}` }}>
        <label style={LABEL}>{isRtl ? 'العنوان بالعربي *' : 'Title in Arabic *'}</label>
        <input style={INPUT} value={form.title_ar} onChange={e => setForm({ ...form, title_ar: e.target.value })} placeholder={isRtl ? 'عرض خاص هذا الأسبوع!' : ''} />

        <label style={LABEL}>{isRtl ? 'العنوان بالإنجليزي' : 'Title in English'}</label>
        <input style={INPUT} value={form.title_en} onChange={e => setForm({ ...form, title_en: e.target.value })} />

        <label style={LABEL}>{isRtl ? 'نص الرسالة بالعربي *' : 'Message in Arabic *'}</label>
        <textarea style={{ ...INPUT, resize: 'none', height: 80 }} value={form.body_ar} onChange={e => setForm({ ...form, body_ar: e.target.value })} />

        <label style={LABEL}>{isRtl ? 'نص الرسالة بالإنجليزي' : 'Message in English'}</label>
        <textarea style={{ ...INPUT, resize: 'none', height: 80 }} value={form.body_en} onChange={e => setForm({ ...form, body_en: e.target.value })} />
      </div>

      <button onClick={send} disabled={sending || !form.title_ar || !form.body_ar} style={{ width: '100%', padding: 14, marginTop: 14, background: sending ? '#C4B5FD' : `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
        {sending ? (isRtl ? 'جارٍ الإرسال...' : 'Sending...') : (isRtl ? '📢 إرسال للعملاء' : '📢 Send to Customers')}
      </button>
    </>
  );
}
